import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { TextButton } from '../Button/Button';
import { Spinner } from '../Button/Button.styles';
import { FieldWrap, Label, Hint } from '../Field/Field.styles';
import { FieldError } from '../Field/Field';
import { ACCEPT_ANY_IMAGE } from '../../onboardingV2.images';
import {
    Tile,
    TileIcon,
    TileTitle,
    TileHint,
    Preview,
    Thumb,
    Optimizing,
    PreviewSide,
    FileName,
    Actions,
} from './ImageDropzone.styles';

/**
 * Single optional photo. The tile is a real `<button>` (keyboard + screen reader friendly);
 * react-dropzone only adds drag & drop. Any image format is accepted; the parent converts it (normalizeImageFile).
 */
const ImageDropzone = ({
    id,
    label,
    hint,
    value,
    optimizing = false,
    disabled = false,
    error,
    onFile,
    onRemove,
}) => {
    const { t } = useTranslation();
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        multiple: false,
        noClick: true,
        noKeyboard: true,
        disabled,
        onDrop: (files) => {
            if (files && files[0]) onFile(files[0]);
        },
    });

    const labelId = `${id}-label`;
    const hintId = `${id}-hint`;
    const describedBy = [error ? `${id}-error` : null, hintId].filter(Boolean).join(' ');

    return (
        <FieldWrap>
            <Label as="span" id={labelId}>
                {label} <small>{t('SignUpV2.common.optional')}</small>
            </Label>
            <div {...getRootProps()}>
                <input {...getInputProps()} id={id} accept={ACCEPT_ANY_IMAGE} />
                {value ? (
                    <Preview>
                        <Thumb>
                            <img src={value.previewUrl} alt={t('SignUpV2.producto.photoAlt')} />
                            {optimizing && (
                                <Optimizing>
                                    <Spinner className="ri-loader-4-line" aria-hidden="true" />
                                    <span>{t('SignUpV2.producto.photoOptimizing')}</span>
                                </Optimizing>
                            )}
                        </Thumb>
                        <PreviewSide>
                            <FileName title={value.name}>{value.name}</FileName>
                            <Actions>
                                <TextButton
                                    icon="ri-refresh-line"
                                    onClick={open}
                                    disabled={disabled || optimizing}
                                >
                                    {t('SignUpV2.producto.photoChange')}
                                </TextButton>
                                <TextButton
                                    icon="ri-delete-bin-6-line"
                                    onClick={onRemove}
                                    disabled={disabled || optimizing}
                                    aria-label={t('SignUpV2.producto.photoRemoveLabel')}
                                >
                                    {t('SignUpV2.producto.photoRemove')}
                                </TextButton>
                            </Actions>
                        </PreviewSide>
                    </Preview>
                ) : (
                    <Tile
                        type="button"
                        onClick={open}
                        disabled={disabled}
                        dragActive={isDragActive}
                        invalid={!!error}
                        aria-labelledby={labelId}
                        aria-describedby={describedBy}
                    >
                        <TileIcon aria-hidden="true">
                            <i className="ri-image-add-line" />
                        </TileIcon>
                        <TileTitle aria-hidden="true">{t('SignUpV2.producto.photoAdd')}</TileTitle>
                        <TileHint aria-hidden="true">{t('SignUpV2.producto.photoDrag')}</TileHint>
                    </Tile>
                )}
            </div>
            <Hint id={hintId}>{hint}</Hint>
            <FieldError id={id} error={error} />
        </FieldWrap>
    );
};

export default ImageDropzone;
